import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { getQuartrLookupIssues, resolveQuartrLookupIssue } from '../../api/contentCompletion';
import { CompanyContentHealth, QuartrLookupIssue } from '../../types/contentCompletion';
import { useNotification } from '../../context/NotificationContext';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const ISSUE_TYPE_LABELS: Record<string, string> = {
  lookup_failed: 'Lookup failed',
  invalid_id: 'Invalid Quartr ID',
  ticker_mismatch: 'Ticker mismatch',
  missing_ticker: 'Missing ticker',
  check_failed: 'Check failed',
  rejected_match: 'Rejected match',
};

const formatIssueType = (value: string) => ISSUE_TYPE_LABELS[value] ?? value.replace(/_/g, ' ');

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

interface QuartrIssuesPanelProps {
  canEdit: boolean;
  refreshToken?: number;
  companies: CompanyContentHealth[];
  onSelectCompany: (company: CompanyContentHealth) => void;
}

const QuartrIssuesPanel: React.FC<QuartrIssuesPanelProps> = ({
  canEdit,
  refreshToken = 0,
  companies,
  onSelectCompany,
}) => {
  const { showNotification } = useNotification();
  const [expanded, setExpanded] = useState(true);
  const [issues, setIssues] = useState<QuartrLookupIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuartrLookupIssues('open');
      setIssues(data.issues);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to load Quartr lookup issues';
      setError(msg);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIssues();
  }, [loadIssues, refreshToken]);

  const handleResolve = async (issueId: string) => {
    setResolvingId(issueId);
    try {
      await resolveQuartrLookupIssue(issueId);
      setIssues((prev) => prev.filter((issue) => issue.id !== issueId));
      showNotification('Quartr issue marked as resolved', 'success');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to resolve issue';
      showNotification(msg, 'error');
    } finally {
      setResolvingId(null);
    }
  };

  const handleOpenCompany = (issue: QuartrLookupIssue) => {
    const existing = companies.find((company) => company.company_id === issue.company_id);
    if (existing) {
      onSelectCompany(existing);
      return;
    }
    onSelectCompany({
      company_id: issue.company_id,
      company_name: issue.company_name,
      ticker: issue.ticker,
      quartr_company_id: issue.quartr_company_id,
      has_feed_configured: false,
      pr_total_count: 0,
      pr_recent_count: 0,
      quartr_total_count: 0,
      quartr_recent_count: 0,
      has_quartr_feed: Boolean(issue.quartr_company_id),
      status: 'no_feed',
      quartr_status: issue.ticker ? 'missing_id' : 'no_feed',
    });
  };

  if (!canEdit && !loading && !error && issues.length === 0) {
    return null;
  }

  const showEmptyState = !loading && !error && issues.length === 0;

  return (
    <Paper sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: expanded ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Unresolved Quartr lookups
          </Typography>
          {!loading && (
            <Chip label={issues.length} size="small" color={issues.length > 0 ? 'warning' : 'default'} />
          )}
          <Typography variant="caption" color="text.secondary">
            Public companies where Quartr ID lookup or validation failed
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? 'Collapse Quartr issues' : 'Expand Quartr issues'}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        {loading ? (
          <Box sx={{ py: 3 }}>
            <LoadingSpinner message="Loading Quartr lookup issues..." />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <ErrorMessage message={error} onDismiss={() => setError(null)} />
          </Box>
        ) : showEmptyState ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No unresolved Quartr lookups yet. Failed Quartr checks are added automatically when you
              run Quartr check on selected companies.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Stored Quartr ID</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>Closest match</TableCell>
                  <TableCell>Reported</TableCell>
                  {canEdit && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow key={issue.id} hover>
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 0 }}
                        onClick={() => handleOpenCompany(issue)}
                      >
                        {issue.company_name}
                      </Button>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {issue.company_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{issue.ticker || '—'}</TableCell>
                    <TableCell>{issue.quartr_company_id || '—'}</TableCell>
                    <TableCell>{formatIssueType(issue.issue_type)}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                        {issue.summary || issue.note || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                        {issue.closest_match || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(issue.reported_at)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        by {issue.reported_by}
                      </Typography>
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={resolvingId === issue.id}
                          onClick={() => handleResolve(issue.id)}
                        >
                          {resolvingId === issue.id ? 'Resolving…' : 'Resolve'}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
};

export default QuartrIssuesPanel;
